import { FieldDef as GenericFieldDef, formOnly as genericFormOnly } from '@field-policy';

import { Declaration, DeclarationHighValueGroupType } from '@e-origin/shared';

import { DECLARATION_CUSTOMS_PROCEDURE_LENGTH, DECLARATION_CUSTOMS_PROCEDURE_SUBSTRING_SEPARATOR } from '@constants';
import { buildChargesImport, buildCommodityCodes, buildContainers, buildDocuments, readIfB2B } from './builders';
import { ConditionalRule, FinalizeSelector } from './types';
import { CHARGES_CURRENCY_MESSAGE, isCurrencyMissing } from './validation';

export type FieldDef = GenericFieldDef<FinalizeSelector, unknown>;

const H2 = DeclarationHighValueGroupType.H2;
const HEADER_TOGGLE = 'goodShipmentGlobal.procedureHeader';

const TRANSPORT = 'transport';

const B2B_NOT_H2: ConditionalRule = { when: { txClass: ['B2B'], not: { group: H2 } }, then: { present: true } };

/**
 * A field hidden unconditionally in some variant (export, or a country the field doesn't apply to)
 * has no `when` that can express that - `flow`/`country`/`isForwarder` aren't selectable (R2). So
 * its base default is `present: false`, and every variant where it actually shows opts in with its
 * own `present: true` (plus this, when the H2 exclusion still applies there).
 */
export const HIDDEN_FOR_H2: ConditionalRule[] = [{ when: { group: H2 }, then: { present: false } }];

/** `isChargesImportVisible`, minus its flow half - see `HIDDEN_FOR_H2`'s comment for why. */
export const CHARGES_HIDDEN: ConditionalRule[] = [
  { when: { not: { txClass: ['B2B'] } }, then: { present: false } },
  { when: { group: H2 }, then: { present: false } },
];

const formOnly = genericFormOnly<FinalizeSelector, unknown>;

const CONTAINER_IDENTIFICATION_NUMBER = {
  label: (i: number) => `Container ${i + 1}`,
  widget: 'text',
  dataPw: (i: number) => `container-list-${i}-input`,
};

export const CONTAINER_ITEMS = {
  identificationNumber: CONTAINER_IDENTIFICATION_NUMBER,
  seals: {
    widget: 'text',
    addButtonLabel: 'Add Seal',
    dataPw: (containerIdx: number) => `container-list-${containerIdx}-seal-list`,
    createNewItem: () => ({ identificationNumber: '' }),
    items: (containerIdx: number) => ({
      identificationNumber: {
        label: (sealIdx: number) => `Seal ${sealIdx + 1}`,
        widget: 'text',
        dataPw: (sealIdx: number) => `container-list-${containerIdx}-seal-${sealIdx}-input`,
      },
    }),
  },
};

export const CONTAINER_ITEMS_NO_SEALS = { identificationNumber: CONTAINER_IDENTIFICATION_NUMBER };

const d = (declaration: unknown) => declaration as Declaration;

const chargesSubtree = (values: unknown) =>
  values as {
    valuationInformationTaxes?: {
      chargesimport?: { CustomsCharges?: { charges?: number | string; currency?: string }; VATcharges?: { charges?: number | string; currency?: string } };
    };
  };

export const FIELDS_BASE = {
  'messageInfo.methodOfPayment': {
    base: { present: false, label: 'Payment Method', code: 'CL104', widget: 'codes', dataPw: 'payment-method-input' },
  },
  'messageInfo.deferredPayment': {
    base: { present: false, label: 'Deferred payment', widget: 'text', dataPw: 'deferred-payment-input' },
  },
  'messageInfo.methodOfPaymentVAT': {
    base: { present: false, label: 'VAT Payment Method', code: 'CL104', widget: 'codes', dataPw: 'vat-payment-method-input' },
  },
  'messageInfo.deferredPaymentVAT': {
    base: { present: false, label: 'Deferred payment VAT', widget: 'text', dataPw: 'vat-deferred-payment-input' },
  },
  'valuationInformationTaxes.valuationMethod': {
    base: { present: false, label: 'Valuation method', code: 'CL604', widget: 'codes', dataPw: 'valuation-method-input' },
  },
  'valuationInformationTaxes.valuationAdjustment.valuationIndicators': {
    base: { present: false, label: 'Valuation indicators', widget: 'text', dataPw: 'valuation-indicators-input' },
  },
  'valuationInformationTaxes.deliveryTerms.incotermCode': {
    base: { present: false, section: TRANSPORT, label: 'Delivery terms', code: 'CL090', widget: 'codes', dataPw: 'transport-delivery-terms-input' },
    rules: [B2B_NOT_H2],
  },
  'valuationInformationTaxes.deliveryTerms.location': {
    base: { present: false, section: TRANSPORT, label: 'Delivery terms place', widget: 'text', dataPw: 'delivery-terms-place-input' },
    rules: [B2B_NOT_H2],
  },
  'valuationInformationTaxes.deliveryTerms.country': {
    base: { present: false, section: TRANSPORT, label: 'Delivery terms country', code: 'CL199', widget: 'codes', dataPw: 'delivery-terms-country-input' },
    rules: [B2B_NOT_H2],
  },
  'declarationGlobal.totalWeight': {
    source: 'none',
    base: { present: true, label: 'Total Weight', widget: 'readonly', dataPw: 'total-weight-input' },
  },
  'declarationGlobal.totalAmountInvoiced': {
    source: 'none',
    base: { present: true, label: 'Total amount invoiced', widget: 'readonly', dataPw: 'total-amount-invoiced-input' },
  },
  'goodShipmentGlobal.invoiceCurrency': {
    source: 'none',
    base: { present: true, label: 'Invoice currency', widget: 'readonly', dataPw: 'invoice-currency-input' },
  },
  'declarationGlobal.totalPCS': {
    source: 'none',
    base: { present: true, label: 'Total PCS', widget: 'readonly', dataPw: 'total-pcs-input' },
  },
  'declarationGlobal.totalCNTS': {
    source: 'none',
    base: { present: true, label: 'Total CTNS', widget: 'readonly', dataPw: 'total-cnts-input' },
  },
  'declarationGoods.packaging.typeOfPackages': {
    source: 'read',
    read: (x) => d(x).goodsShipmentItems?.[0]?.packaging?.typeOfPackages,
    viewModel: true, // saveData sends declarationGoods as partialGoodsShipmentItem, not in the declaration
    base: { present: false, label: 'Package Type', code: 'CL017', widget: 'codes', dataPw: 'package-type-input' },
    rules: [{ when: { txClass: ['B2B'] }, then: { present: true } }],
  },

  'messageInfo.declarationMainType': {
    base: { present: true, label: 'Declaration Main Type', code: 'CL231', widget: 'codes', dataPw: 'declaration-main-type-input' },
  },
  'messageInfo.declarationType': {
    base: { present: true, label: 'Declaration Type', code: 'CL042', widget: 'codes', dataPw: 'declaration-type-input' },
  },
  'messageInfo.security': {
    base: {
      present: false,
      label: 'Security',
      code: 'DeclarationTypeSecurity',
      widget: 'codes',
      coerce: 'number',
      dataPw: 'security-input',
    },
  },
  customer: {
    base: { present: true, label: 'Customer', widget: 'text', dataPw: 'customer-input' },
  },
  'customsState.bcpMrn': {
    base: { present: false, label: 'BCP MRN', widget: 'text', dataPw: 'bcp-mrn-input' },
    rules: [{ when: { txClass: ['B2B'] }, then: { present: true } }],
  },
  'messageInfo.presentationNotificationEstimatedDateTime': {
    base: { present: false, label: 'Estimated arrival date and time', widget: 'datetime', dataPw: 'presentation-notification-estimated-date-time-input' },
  },
  'consignment.customsOfficePresentation.referenceNumber': {
    base: {
      present: false,
      label: 'Customs Presentation Office',
      code: 'CL141',
      widget: 'codes',
      dataPw: 'customs-office-presentation-input',
    },
  },
  'consignment.customsOfficeExit.referenceNumber': {
    base: {
      present: false,
      label: 'Customs Exit office',
      code: 'CustomsOfficeExitDeclared',
      widget: 'codes',
      dataPw: 'customs-office-exit-input',
    },
  },
  'consignment.customsOfficeExport.referenceNumber': {
    base: {
      present: false,
      label: 'Customs Export office',
      code: 'CustomsOfficeExport',
      widget: 'codes',
      dataPw: 'customs-office-export-input',
    },
  },
  'consignment.customsSupervisingOffice.referenceNumber': {
    base: {
      present: false,
      label: 'Supervising customs office',
      code: 'CL141',
      widget: 'codes',
      dataPw: 'supervising-customs-office-input',
    },
  },
  'consignment.locationOfGoods.unLocode': {
    base: { present: true, label: 'Goods location', widget: 'text', dataPw: 'goods-location-input' },
  },
  warehouse: {
    source: 'read',
    read: (x) => d(x).documents?.warehouseDocuments?.[0]?.identifier || '',
    viewModel: true,
    base: { present: false, label: 'Bonded warehouse', widget: 'text' },
    rules: [{ when: { group: H2 }, then: { present: true } }],
  },
  'generalInfo.group': {
    base: { present: false, label: 'Declaration profile', widget: 'text', dataPw: 'message-type-input' },
  },
  customsProcedureCurrent: {
    source: 'read',
    read: (x) => d(x).goodShipmentGlobal?.customsProcedure?.substring(0, DECLARATION_CUSTOMS_PROCEDURE_SUBSTRING_SEPARATOR),
    viewModel: true,
    base: {
      present: true,
      label: 'Requested procedure',
      code: 'CL092',
      widget: 'codes',
      headerToggle: HEADER_TOGGLE,
      dataPw: 'requested-procedure-input',
    },
  },
  customsProcedurePrevious: {
    source: 'read',
    read: (x) => d(x).goodShipmentGlobal?.customsProcedure?.substring(DECLARATION_CUSTOMS_PROCEDURE_SUBSTRING_SEPARATOR, DECLARATION_CUSTOMS_PROCEDURE_LENGTH),
    viewModel: true,
    base: {
      present: true,
      label: 'Previous procedure',
      code: 'CL093',
      widget: 'codes',
      headerToggle: HEADER_TOGGLE,
      dataPw: 'previous-procedure-input',
    },
  },
  'goodShipmentGlobal.preference': {
    base: {
      present: false,
      label: 'Preference',
      code: 'CL603',
      widget: 'codes',
      headerToggle: HEADER_TOGGLE,
      dataPw: 'preference-input',
    },
  },
  'declarationGoods.additionalProcedure': {
    source: 'read',
    read: (x) => d(x).goodsShipmentItems?.[0]?.additionalProcedure || [],
    viewModel: true,
    base: {
      present: true,
      label: 'Additional procedure',
      code: 'AdditionalProcedure',
      widget: 'codes-multi',
      dataPw: 'additional-procedure-input',
    },
  },
  'goodShipmentGlobal.customsProcedureType': {
    base: {
      present: false,
      label: 'Procedure Type',
      widget: 'text',
      headerToggle: HEADER_TOGGLE,
      dataPw: 'procedure-type-input',
    },
  },
  'goodShipmentGlobal.customsNationalProcedure': {
    base: {
      present: false,
      label: 'National procedure',
      widget: 'text',
      headerToggle: HEADER_TOGGLE,
      dataPw: 'national-procedure-input',
    },
  },
  'goodShipmentGlobal.customsNationalProcedureCode': {
    base: {
      present: false,
      label: 'National procedure code',
      code: 'CL457',
      widget: 'codes',
      headerToggle: HEADER_TOGGLE,
      dataPw: 'national-procedure-code-input',
    },
  },
  'goodShipmentGlobal.customsProcedure': formOnly(),
  'goodShipmentGlobal.procedureHeader': formOnly({
    source: 'read',
    read: (x) => d(x).goodShipmentGlobal?.procedureHeader ?? true,
  }),
  'consignment.countryOfDispatch': {
    base: {
      present: false,
      label: 'Country of Dispatch',
      code: 'CL199',
      widget: 'codes',
      headerToggle: 'consignment.countryOfDispatchHeader',
      dataPw: 'country-of-dispatch-input',
    },
  },
  'consignment.countryOfExport': {
    base: {
      present: false,
      label: 'Country of Export',
      code: 'CL008',
      widget: 'codes',
      headerToggle: 'consignment.countryOfExportHeader',
      dataPw: 'country-of-export-input',
    },
  },
  'consignment.countryOfDestination': {
    base: {
      present: false,
      label: 'Country of Destination',
      code: 'CL010',
      widget: 'codes',
      headerToggle: 'consignment.destinationHeader',
      dataPw: 'country-of-destination-input',
    },
    rules: [{ when: { txClass: ['B2B'] }, then: { present: true } }],
  },
  'consignment.regionOfDestination': {
    base: {
      present: false,
      label: 'Region of Destination',
      widget: 'text',
      headerToggle: 'consignment.destinationHeader',
      dataPw: 'region-of-destination-input',
    },
  },
  'consignment.carrier': {
    base: { present: false, label: 'Carrier EORI', widget: 'text', headerToggle: 'consignment.carrierHeader', dataPw: 'carrier-input' },
  },

  'consignment.carrierHeader': formOnly(),
  'consignment.countryOfDispatchHeader': formOnly({ source: 'read', read: readIfB2B((x) => x.consignment?.countryOfDispatchHeader) }),
  'consignment.countryOfExportHeader': formOnly({ source: 'read', read: readIfB2B((x) => x.consignment?.countryOfExportHeader) }),
  'consignment.destinationHeader': formOnly({ source: 'read', read: readIfB2B((x) => x.consignment?.destinationHeader) }),

  'transportInformation.departureTransportMeans.identificationNumber': {
    base: { present: false, label: 'Departure transport means identification', widget: 'text', dataPw: 'departure-transport-means-identification-input' },
  },
  'transportInformation.departureTransportMeans.typeOfIdentification': {
    base: { present: false, label: 'Departure transport means type', code: 'CL750', widget: 'codes', dataPw: 'departure-transport-means-type-input' },
  },
  'transportInformation.departureTransportMeans.nationality': {
    base: {
      present: false,
      label: 'Departure transport means nationality',
      code: 'CL165',
      widget: 'codes',
      dataPw: 'departure-transport-means-nationality-input',
    },
  },
  'transportInformation.activeBorderTransportMeans.identificationNumber': {
    base: { present: false, label: 'Active border transport means ID', widget: 'text', dataPw: 'active-border-transport-means-identification-input' },
  },
  'transportInformation.activeBorderTransportMeans.typeOfIdentification': {
    base: {
      present: false,
      label: 'Active border transport means type',
      code: 'TypeOfIdentificationofMeansOfTr',
      widget: 'codes',
      dataPw: 'active-border-transport-means-type-input',
    },
  },
  'transportInformation.activeBorderTransportMeans.nationality': {
    base: {
      present: true,
      label: 'Active border transport means nationality',
      code: 'CL165',
      widget: 'codes',
      dataPw: 'active-border-transport-means-nationality-input',
    },
    rules: [{ when: { group: H2 }, then: { present: false } }],
  },
  'transportInformation.modeOfTransportMeansAtTheBorder': {
    base: { present: true, label: 'Transport mode at border', code: 'CL018', widget: 'codes', dataPw: 'transport-mode-at-border-input' },
  },
  'transportInformation.arrivalTransportMeans.identificationNumber': {
    base: { present: false, label: 'Arrival transport means ID', widget: 'text', dataPw: 'arrival-transport-means-identification-input' },
  },
  'transportInformation.arrivalTransportMeans.typeOfIdentification': {
    base: { present: false, label: 'Arrival Transport means type', code: 'CL750', widget: 'codes', dataPw: 'arrival-transport-means-type-input' },
  },
  'transportInformation.inlandModeOfTransport': {
    base: { present: true, label: 'Inland transport mode', code: 'CL018', widget: 'codes', dataPw: 'inland-transport-mode-input' },
    rules: [{ when: { group: H2 }, then: { present: false } }],
  },
  loadingPlaceIdentity: {
    source: 'none',
    base: { present: false, label: 'Loading Place Identity', widget: 'text', dataPw: 'loading-place-identity-input' },
  },

  'transportInformation.transportEquipment.header': formOnly(),
  'transportInformation.transportEquipment.containerIdentification': formOnly(),
  'transportInformation.countryOfRoutingOfConsignment': {
    base: { present: false, label: 'Routing of Consignment', widget: 'array', dataPw: 'routing-of-consignment-country' },
    source: 'read',
    read: (x) => {
      const routing = d(x).transportInformation?.countryOfRoutingOfConsignment;
      return routing?.length ? routing : [{ country: '' }];
    },
    items: {
      country: { label: 'Country', widget: 'codes', code: 'CL008', dataPw: (i) => `routing-of-consignment-country-${i}` },
    },
    createNewItem: () => ({ country: '' }),
  },
  'transportInformation.transportEquipment.containers': {
    base: {
      present: true,
      label: 'Containers',
      widget: 'array',
      dataPw: 'container-list',
      headerToggle: 'transportInformation.transportEquipment.header',
    },
    source: 'read',
    read: (x) => buildContainers(d(x).transportInformation?.transportEquipment),
    items: CONTAINER_ITEMS,
    createNewItem: () => ({ identificationNumber: '', seals: [] }),
  },
  'valuationInformationTaxes.chargesimport': formOnly({ source: 'read', read: (x) => buildChargesImport(d(x)) }),
  documents: formOnly({ source: 'read', read: (x) => buildDocuments(d(x)) }),
  commodityCodes: formOnly({ source: 'read', read: (x) => buildCommodityCodes(d(x)), viewModel: true }),
  chargesDeliveryTerms: {
    source: 'none',
    base: { present: false, label: 'Delivery terms', widget: 'readonly', dataPw: 'delivery-terms-input' },
  },
  'valuationInformationTaxes.chargesimport.CustomsCharges.charges': {
    source: 'computed',
    base: { present: false, label: 'Customs charges', widget: 'number', dataPw: 'customs-charges-input' },
  },
  'valuationInformationTaxes.chargesimport.CustomsCharges.currency': {
    source: 'computed',
    base: { present: false, label: 'Currency', code: 'Currency', widget: 'codes', dataPw: 'customs-charges-currency-input' },
    validate: (values) => {
      const charges = chargesSubtree(values).valuationInformationTaxes?.chargesimport?.CustomsCharges;

      return isCurrencyMissing(charges?.charges, charges?.currency) ? CHARGES_CURRENCY_MESSAGE : undefined;
    },
  },
  'valuationInformationTaxes.chargesimport.VATcharges.charges': {
    source: 'computed',
    base: { present: false, label: 'VAT charges', widget: 'number', dataPw: 'vat-charges-input' },
  },
  'valuationInformationTaxes.chargesimport.VATcharges.currency': {
    source: 'computed',
    base: { present: false, label: 'Currency', code: 'Currency', widget: 'codes', dataPw: 'vat-charges-currency-input' },
    validate: (values) => {
      const charges = chargesSubtree(values).valuationInformationTaxes?.chargesimport?.VATcharges;

      return isCurrencyMissing(charges?.charges, charges?.currency) ? CHARGES_CURRENCY_MESSAGE : undefined;
    },
  },
} as const satisfies Record<string, FieldDef>;

export type FieldPath = keyof typeof FIELDS_BASE;

export type PolicyFieldPath = { [K in FieldPath]: (typeof FIELDS_BASE)[K] extends { base: object } ? K : never }[FieldPath];
